/**
 * Adaptador entre `/customers/dropdown` e o `SearchableSelect`.
 *
 * `document` é gravado no banco só com dígitos (`digitsOnly` no Zod de
 * customers), então a máscara é aplicada aqui, na leitura — mesma função que o
 * cadastro completo usa, pra CPF/CNPJ aparecerem iguais nas duas telas.
 */
import type { CustomerDropdownItem } from '@troia-v3/types';

import type { SearchableSelectOption } from '@/shared/components/ui/SearchableSelect';
import { formatDocumentInput } from '@/shared/utils/documentMask';

/** `GET /customers/dropdown` tem `.limit(100)` fixo e NÃO pagina (C3). */
export const CUSTOMER_DROPDOWN_LIMIT = 100;

export function buildCustomerOptions(items: CustomerDropdownItem[]): SearchableSelectOption[] {
  return items.map((customer) => {
    const sublabel = customer.document ? formatDocumentInput(customer.document, customer.type) : undefined;
    return {
      id: customer.id,
      label: customer.name,
      ...(sublabel ? { sublabel } : {}),
    };
  });
}

/** Lista veio no teto do limite: pode haver cliente invisível — avisar no rodapé. */
export function isCustomerListFull(items: CustomerDropdownItem[]): boolean {
  return items.length >= CUSTOMER_DROPDOWN_LIMIT;
}

/** Só dígitos + pontuação de documento (`.`, `/`, `-`, espaço) — nenhuma letra. */
const DOCUMENT_LIKE_TERM = /^[\d.\-/\s]+$/;

/**
 * Antes de mandar o termo pro `search` do `/customers/dropdown`: o backend
 * faz regex LITERAL contra `document`, que no banco é só dígitos (Zod
 * `digitsOnly`, Task 2 do Plano C). Buscar "12.345.678/0001-99" (como o
 * placeholder do campo sugere) contra "12345678000199" gravado dá ZERO
 * resultados — a busca promete achar por CNPJ e falha em silêncio.
 *
 * Se o termo for majoritariamente dígitos com pontuação de documento, remove
 * a pontuação antes de buscar. Termo com letra (nome) passa direto — inclusive
 * nomes com hífen ("Ac-me"), que não têm dígito nenhum e não batem no regex.
 * Pontuação isolada sem dígito suficiente (ex.: `"-"` sozinho) também passa
 * direto: virar string vazia mandaria a lista inteira pro painel em vez de
 * "nenhum resultado" para esse tipo de digitação incidental.
 */
export function sanitizeCustomerSearchTerm(term: string): string {
  const trimmed = term.trim();
  if (!trimmed) return trimmed;
  if (!DOCUMENT_LIKE_TERM.test(trimmed)) return term;

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 3) return term;

  return digits;
}
